import { Box, type Theme, useTheme } from "@mui/material";
import ticketNoData from "assets/images/nodata/ticketNoData.png";
import { StackedBarChart } from "components/charts";
import { useGetReportGraphData, useGlobalStore } from "controller";
import { useAtomValue } from "jotai";
import { useMemo } from "react";
import { useDashboardScope } from "../../../hooks/useDashboardScope";
import { activityPeriodAtom } from "../../../store";
import type { ActivityPeriod } from "../../../types";
import { ActivityCard } from "./ActivityCard";
import { ActivityEmptyState } from "./ActivityEmptyState";
import { ActivityLegend } from "./ActivityLegend";

/**
 * Bar segment colour per status label, mirroring the status badges so a segment
 * reads the same colour the user sees on the ticket itself. Statuses whose badge
 * has no text colour (badge is a solid fill) reuse that fill. The report API
 * sends only the status label — not an id — so this is keyed by the lowercased
 * label. `#907BFF` / `#8791F8` / `#F5E755` have no theme token — they are copied
 * from `StatusBadge` on purpose so both stay in sync.
 */
const getStatusSegmentColors = (
  theme: Theme,
): Record<string, string | undefined> => ({
  new: theme.palette.info.main,
  assigned: "#907BFF",
  pending: theme.palette.warning.main,
  "in progress": theme.palette.purple.hover,
  closed: theme.palette.success.main,
  completed: theme.palette.primitive.system.green[400],
  reopened: theme.palette["warning-minor"].main,
  "re-opened": theme.palette["warning-minor"].main,
  cancelled: "#8791F8",
  merged: "#F5E755",
  escalated: theme.palette.primitive.system.red[800],
  others: theme.palette.primary.pressed,
});

/**
 * Display order for segments and legend, fixed by design rather than by the
 * order the report API happens to return. Statuses outside this list keep their
 * API order behind the known ones, and the "Others" catch-all is always last.
 */
const STATUS_ORDER = [
  "new",
  "assigned",
  "in progress",
  "escalated",
  "waiting on customer",
  "pending",
  "re-opened",
  "reopened",
  "closed",
  "completed",
  "cancelled",
  "merged",
];

const getStatusRank = (label: string): number => {
  const key = label?.toLowerCase() ?? "";
  if (key === "others") return Number.MAX_SAFE_INTEGER;
  const index = STATUS_ORDER.indexOf(key);
  return index === -1 ? STATUS_ORDER.length : index;
};

/** The report API spells its periods the same way the filter menu does. */
const toReportPeriod = (period: ActivityPeriod): string => period;

interface PriorityBucket {
  title: string;
  data: Array<{ label: string; value: number; color?: string }>;
}

export const TicketByPriorityCard = () => {
  const theme = useTheme();
  const timezone = useGlobalStore((state) => state.timezone);
  const period = useAtomValue(activityPeriodAtom);
  const { reportResource, isReady } = useDashboardScope();

  // Held until the scope resolves — firing early would show tenant-wide bars for
  // a beat before the scoped ones replace them.
  //
  // POST is not optional here: the GET route for `tickets-by-priority` has always
  // discarded `resource`, so over GET this card silently reports tenant-wide
  // numbers no matter who is selected.
  const { data, isFetching } = useGetReportGraphData(
    {
      reportName: "tickets-by-priority",
      timePeriod: toReportPeriod(period),
      serviceType: "ticket",
      timezone,
      resource: reportResource,
      filterAvailable: ["Resource"],
      method: "POST",
    },
    isReady,
  );

  // The chart wants a colour on every segment. Statuses outside the default set
  // fall back to neutral rather than a colour that clashes with the badges, and
  // every bucket is stacked in the fixed design order so the legend below can
  // mirror it.
  const buckets = useMemo<PriorityBucket[]>(() => {
    const segmentColors = getStatusSegmentColors(theme);
    if (!Array.isArray(data)) return [];

    return data.map((bucket: { title: string; data?: Array<unknown> }) => ({
      title: bucket.title,
      data: (bucket.data ?? [])
        .map((entry) => {
          const status = entry as { label: string; value: number };
          return {
            label: status.label,
            value: status.value,
            color:
              segmentColors[status.label?.toLowerCase()] ||
              theme.palette.primitive.neutrals.neutral[300],
          };
        })
        .sort((a, b) => getStatusRank(a.label) - getStatusRank(b.label)),
    }));
  }, [data, theme]);

  // The API echoes back every status on every bucket, zero-valued ones included,
  // so the first bucket's list is the full status set rather than what this
  // period actually contains. Keep only statuses that carry a value somewhere in
  // the chart — colours (and the "Others" catch-all) come straight from the
  // buckets, so both stay in sync with the bars.
  const legendItems = useMemo(() => {
    const seen = new Set<string>();
    const items: Array<{ label: string; color?: string }> = [];

    for (const bucket of buckets) {
      for (const entry of bucket.data) {
        if (!entry.value || seen.has(entry.label)) continue;
        seen.add(entry.label);
        items.push({ label: entry.label, color: entry.color });
      }
    }

    // Buckets are already sorted, but a status can first appear in a later
    // bucket, so rank the collected list again.
    return items.sort(
      (a, b) => getStatusRank(a.label) - getStatusRank(b.label),
    );
  }, [buckets]);

  // Every priority bucket comes back with the full status set echoed at zero, so
  // a non-empty bucket list is not proof of activity — the values are.
  const hasData = useMemo(
    () =>
      buckets.some((bucket) => bucket.data.some((entry) => entry.value > 0)),
    [buckets],
  );

  // While the scope is still resolving the query is held, not empty.
  const isEmpty = !isFetching && isReady && !hasData;

  return (
    <ActivityCard title={"Tickets by priority"}>
      {/* Flex only for the empty state, so it centres in the full body height —
          the chart sizes itself off the block box and is left alone. */}
      <Box
        sx={{
          display: isEmpty ? "flex" : "block",
          flex: 1,
          minHeight: "200px",
        }}
      >
        {isEmpty ? (
          <ActivityEmptyState
            image={ticketNoData}
            title="No tickets yet"
            description="No ticket activity to display."
            imageWidth="118px"
          />
        ) : (
          <StackedBarChart
            data={buckets}
            labelKey="title"
            valueKey="values"
            width="100%"
            height="100%"
            isLoading={isFetching}
            barRadius={4}
            barSize={24}
            showLegends={false}
            showAxisLines
            showGrid
          />
        )}
      </Box>

      {legendItems.length > 0 && <ActivityLegend items={legendItems} />}
    </ActivityCard>
  );
};
